/**
 * Instrucciones de instalación de la PWA, en criollo y por navegador.
 *
 * @module utils/installSteps
 * @version 6.11.43.00
 *
 * Por qué existe: el botón "Instalar" solo funciona donde el navegador dispara
 * `beforeinstallprompt` **y** además honra `prompt()` (en la práctica: Chrome y
 * derivados en Android). En Safari no existe la API, y en Samsung Internet el
 * evento llega pero `prompt()` no muestra nada. En esos casos lo único que
 * queda es explicarle al jugador qué tocar — y para eso hay que saber
 * exactamente cómo se llama cada opción en SU navegador, porque "Agregar a
 * pantalla de inicio" no aparece igual en Safari que en Samsung.
 *
 * Módulo puro (recibe el user agent por parámetro, no toca `navigator` salvo en
 * el wrapper del final) para poder probarlo sin DOM: `npm run test:install`.
 */

/** Navegador/plataforma a efectos de "cómo se instala acá". */
export type InstallPlatform =
  | 'ios-safari'
  | 'ios-other'
  | 'samsung'
  | 'android-chrome'
  | 'android-firefox'
  | 'android-other'
  | 'desktop';

/** Íconos disponibles para decorar el "chip" de un paso. */
export type InstallStepIcon = 'dots' | 'share' | 'more' | 'home' | 'plus' | 'check';

export interface InstallStep {
  /** Qué tiene que hacer, en lenguaje de persona. */
  text: string;
  /** La etiqueta EXACTA que va a ver en pantalla (se dibuja como chip). */
  chip?: string;
  /** Ícono decorativo del chip. */
  icon?: InstallStepIcon;
  /** Aclaración chiquita, para el que no encuentra el botón. */
  hint?: string;
}

export interface InstallGuide {
  platform: InstallPlatform;
  /** Nombre lindo del navegador, para titular los pasos. */
  browserLabel: string;
  /**
   * `true` si el navegador puede instalar con un toque. Ojo: es la capacidad
   * *teórica* del navegador; que el evento haya llegado lo sabe `usePWAInstall`.
   */
  supportsNativePrompt: boolean;
  steps: InstallStep[];
  /** Cierre festivo, después del último paso. */
  closing: string;
  /** Variante frecuente del menú, para el que ve otra cosa. */
  note?: string;
}

// ---------------------------------------------------------------------------
// Detección
// ---------------------------------------------------------------------------

/**
 * Qué navegador es, a los fines de las instrucciones.
 *
 * Orden de los chequeos: los tokens más específicos primero. `SamsungBrowser`
 * incluye `Chrome` en su UA, y `CriOS`/`FxiOS` son Chrome/Firefox en iPhone
 * (misma WebKit que Safari, pero con otro menú).
 */
export const detectInstallPlatform = (userAgent: string): InstallPlatform => {
  const ua = userAgent || '';
  const lower = ua.toLowerCase();

  const isIOS = /iphone|ipad|ipod/.test(lower);
  const isAndroid = /android/.test(lower);

  if (isIOS) {
    // Safari es el único que no se anuncia: se lo reconoce por descarte.
    const isOtherIOSBrowser = /crios|fxios|edgios|opios|yabrowser|duckduckgo/.test(lower);
    return isOtherIOSBrowser ? 'ios-other' : 'ios-safari';
  }

  if (isAndroid) {
    if (/samsungbrowser/.test(lower)) return 'samsung';
    if (/firefox|fxios/.test(lower)) return 'android-firefox';
    // Edge (edga), Opera (opr) y Brave usan el mismo menú que Chrome.
    if (/chrome|crios|edga|opr\//.test(lower)) return 'android-chrome';
    return 'android-other';
  }

  return 'desktop';
};

// ---------------------------------------------------------------------------
// Contenido
// ---------------------------------------------------------------------------

const CLOSING_HOME =
  'Listo: el ícono de Liga Ajupam queda en tu pantalla de inicio. Abrila desde ahí y entrás directo.';

/**
 * Pasos de Safari. Están armados alrededor de los tres puntitos (⋯) porque es
 * lo que muestran los iPhone actuales en la barra de abajo; el ícono de
 * Compartir suelto quedó en las versiones viejas y se cubre con la nota.
 */
const IOS_SAFARI_STEPS: InstallStep[] = [
  {
    text: 'Tocá los tres puntitos',
    chip: '•••',
    icon: 'dots',
    hint: 'Abajo a la derecha, en la barra de Safari.',
  },
  {
    text: 'Tocá',
    chip: 'Compartir',
    icon: 'share',
  },
  {
    text: 'Bajá un poco y tocá',
    chip: 'Ver más',
    icon: 'more',
    hint: 'Si ya ves "Añadir a inicio" en la lista, salteá este paso.',
  },
  {
    text: 'Tocá',
    chip: 'Añadir a inicio',
    icon: 'plus',
  },
  {
    text: 'Confirmá arriba a la derecha con',
    chip: 'Añadir',
    icon: 'check',
  },
];

/** Samsung Internet: menú ⋮ abajo a la derecha (en versiones viejas, ≡). */
const SAMSUNG_STEPS: InstallStep[] = [
  {
    text: 'Tocá los tres puntitos',
    chip: '⋮',
    icon: 'dots',
    hint: 'Abajo a la derecha, en la barra del navegador.',
  },
  {
    text: 'Elegí',
    chip: 'Añadir a',
    hint: 'En algunos teléfonos dice "Agregar página a".',
  },
  {
    text: 'Tocá',
    chip: 'Pantalla de inicio',
    icon: 'home',
  },
  {
    text: 'Confirmá con',
    chip: 'Añadir',
    icon: 'check',
  },
];

const CHROME_STEPS: InstallStep[] = [
  {
    text: 'Tocá los tres puntitos',
    chip: '⋮',
    icon: 'dots',
    hint: 'Arriba a la derecha, al lado de la dirección.',
  },
  {
    text: 'Elegí',
    chip: 'Instalar aplicación',
    icon: 'plus',
    hint: 'En algunos teléfonos dice "Agregar a la pantalla principal".',
  },
  {
    text: 'Confirmá con',
    chip: 'Instalar',
    icon: 'check',
  },
];

const FIREFOX_STEPS: InstallStep[] = [
  {
    text: 'Tocá los tres puntitos',
    chip: '⋮',
    icon: 'dots',
    hint: 'Abajo a la derecha, en la barra de Firefox.',
  },
  {
    text: 'Elegí',
    chip: 'Instalar',
    icon: 'plus',
    hint: 'En algunas versiones dice "Agregar a la pantalla de inicio".',
  },
  {
    text: 'Confirmá con',
    chip: 'Agregar',
    icon: 'check',
  },
];

const GENERIC_ANDROID_STEPS: InstallStep[] = [
  {
    text: 'Abrí el menú del navegador',
    chip: '⋮',
    icon: 'dots',
    hint: 'Suele estar arriba o abajo a la derecha.',
  },
  {
    text: 'Buscá la opción',
    chip: 'Agregar a pantalla de inicio',
    icon: 'plus',
    hint: 'También puede decir "Instalar aplicación".',
  },
  {
    text: 'Confirmá con',
    chip: 'Agregar',
    icon: 'check',
  },
];

/**
 * Las guías, por plataforma.
 *
 * `ios-other` (Chrome/Edge/Firefox en iPhone) es un caso aparte: ahí el ícono
 * que se agrega no queda bien enganchado a la app y las notificaciones no
 * llegan, así que el primer paso es mudarse a Safari y recién después van los
 * pasos de siempre.
 */
export const getInstallGuide = (platform: InstallPlatform): InstallGuide => {
  switch (platform) {
    case 'ios-safari':
      return {
        platform,
        browserLabel: 'Safari',
        supportsNativePrompt: false,
        steps: IOS_SAFARI_STEPS,
        closing: CLOSING_HOME,
        note: 'Si tu iPhone muestra el ícono de Compartir (un cuadradito con una flecha para arriba) en vez de los tres puntitos, tocá ese y seguí desde el paso 3.',
      };

    case 'ios-other':
      return {
        platform,
        browserLabel: 'Safari',
        supportsNativePrompt: false,
        steps: [
          {
            text: 'Abrí esta misma página en',
            chip: 'Safari',
            icon: 'share',
            hint: 'Copiá el enlace de acá abajo y pegalo en Safari. Desde otros navegadores el iPhone no instala la app.',
          },
          ...IOS_SAFARI_STEPS,
        ],
        closing: CLOSING_HOME,
      };

    case 'samsung':
      return {
        platform,
        browserLabel: 'Samsung Internet',
        // El evento llega, pero prompt() no abre nada. Instalación a mano.
        supportsNativePrompt: false,
        steps: SAMSUNG_STEPS,
        closing: CLOSING_HOME,
        note: 'Si en lugar de los tres puntitos ves tres rayitas (≡), es ese mismo menú.',
      };

    case 'android-chrome':
      return {
        platform,
        browserLabel: 'Chrome',
        supportsNativePrompt: true,
        steps: CHROME_STEPS,
        closing: CLOSING_HOME,
      };

    case 'android-firefox':
      return {
        platform,
        browserLabel: 'Firefox',
        supportsNativePrompt: false,
        steps: FIREFOX_STEPS,
        closing: CLOSING_HOME,
      };

    case 'android-other':
      return {
        platform,
        browserLabel: 'tu navegador',
        supportsNativePrompt: true,
        steps: GENERIC_ANDROID_STEPS,
        closing: CLOSING_HOME,
      };

    case 'desktop':
    default:
      return {
        platform: 'desktop',
        browserLabel: 'tu navegador',
        supportsNativePrompt: true,
        steps: GENERIC_ANDROID_STEPS,
        closing: CLOSING_HOME,
      };
  }
};

/** Atajo para el runtime: detecta y devuelve la guía en un solo paso. */
export const getInstallGuideForCurrentBrowser = (): InstallGuide =>
  getInstallGuide(detectInstallPlatform(typeof navigator === 'undefined' ? '' : navigator.userAgent));
